import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { captureMessage } from '@sentry/nextjs';
import { isAxiosError } from 'axios';
import { IContainer } from '@/shared/config/Container';
import { PaymentMethodType } from '@/core/domain/services/IPaymentService';
import type { paymentMethodTypeSchema } from '@/app/(unauth)/order/[orderId]/pay/_components/PaymentMethod';
import type z from 'zod';
import {
  ApiPaymentService,
  isPlanWalletConsentPending,
  type StoreCreditPlanPaymentConsentPending,
  type AvailablePaymentMethod,
  type CheckoutConsentProvider,
  type PaymentInitializationParams,
  type PaymentStatusPollResult,
  type StoreCreditPlanPaymentApplyRequest,
  type StoreCreditPlanPaymentApplyResponse,
  type StoreCreditPlanPaymentPreviewRequest,
  type StoreCreditPlanPaymentPreviewResponse,
} from '@/core/infrastructure/services/ApiPaymentService';
import { getApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { normalizeMoneyString } from '@/shared/utils/price';
import {
  checkoutObservationEpoch, getKnownCheckoutPaymentObservations, readCheckoutPaymentObservation, removeCheckoutPaymentObservation,
  saveCheckoutPaymentObservation, setCheckoutObservationAccount,
} from '@/shared/utils/checkoutPaymentObservation';

export interface PaymentMethodsType {
  id: string;
  name: string;
  type: PaymentMethodType;
  icon: string;
  isAvailable: boolean;
  minimumAmount?: number;
  maximumAmount?: number;
  processingFee?: number;
}

export interface PaymentTransaction {
  id: string;
  orderId: string;
  installmentId?: string;
  amount: number;
  method: PaymentMethodType;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  reference?: string;
  phoneNumber?: string;
  createdAt: Date;
  updatedAt: Date;
  failureReason?: string;
  customerPaymentDecision?: PaymentStatusPollResult['customerPaymentDecision'];
  customerPaymentOutcome?: PaymentStatusPollResult['customerPaymentOutcome'];
  details?: PaymentInitiationRequest,
  provider: string;
  instructions?: string
}

export type PaymentIntent = 'checkout' | 'installment' | 'advance' | 'payoff' | 'additional_charge';

export interface PaymentInitiationRequest {
  amount: number;
  orderId: string;
  installmentId?: string;
  chargeId?: string;
  recoveryPaymentContext?: {
    paymentPlanId?: string;
    recoveryOptionId: string;
    recoveryReliefId?: string;
  };
  walletPlanApplication?: {
    applicationId: string;
    amountApplied: number;
    selectedAmount?: number;
    providerIdempotencyKey?: string;
  };
  phoneNumber: string;
  method: PaymentMethodType;
  provider: string;
  idempotencyKey: string;
  autoDebitAuthorized?: boolean;
  intent?: PaymentIntent;
}

export interface PaymentVerificationRequest {
  transactionId: string;
  reference: string;
  method: PaymentMethodType;
}

export interface PaymentResponse {
  success: boolean;
  superseded?: boolean;
  transactionId?: string;
  reference?: string;
  message: string;
  redirectUrl?: string;
  pollInterval?: number;
}

export interface PlanPaymentProviderRecoveryPolicy {
  intervalMs: number;
  requestTimeoutMs: number;
  windowMs: number;
}

const PLAN_PAYMENT_PROVIDER_RECOVERY_WINDOW_MS = 90_000;
const PLAN_PAYMENT_PROVIDER_STATUS_TIMEOUT_MS = 15_000;
const MIN_PLAN_PAYMENT_PROVIDER_INTERVAL_SECONDS = 5;
const MAX_PLAN_PAYMENT_PROVIDER_INTERVAL_SECONDS = 60;

function customerPaymentSucceeded(
  decision: PaymentStatusPollResult['customerPaymentDecision'],
): boolean {
  return decision === 'completed';
}

function customerPaymentUnavailable(
  decision: PaymentStatusPollResult['customerPaymentDecision'],
): boolean {
  return decision === 'unavailable';
}

function customerPaymentFailureReason(
  response: PaymentStatusPollResult,
): string | undefined {
  const status = String(response.status || '').toLowerCase();
  if (!['failed', 'cancelled', 'refunded'].includes(status)) return undefined;
  return response.statusDisplay || 'Payment failed';
}

class PaymentStore {
  planWalletConsent: StoreCreditPlanPaymentConsentPending | null = null;
  planWalletConsentResult: StoreCreditPlanPaymentApplyResponse | null = null;
  planWalletConsentPollingPaused = false;
  planWalletConsentFailed = false;
  isCheckingPlanWalletConsent = false;
  planWalletConsentError: string | null = null;
  private planWalletConsentRequest: StoreCreditPlanPaymentApplyRequest | null = null;
  private planWalletConsentTimer: ReturnType<typeof setTimeout> | null = null;
  private planWalletConsentDeadline = 0;
  private planWalletConsentGeneration = 0;
  private planWalletConsentWarningEmitted = false;
  // Observable state
  isLoading = false;
  error: string | null = null;
  private lastVerificationPolicy: { pollIntervalMs: number; timeoutSeconds: number } | null = null;

  // Payment data
  availablePaymentMethodsTypes: PaymentMethodType[] = [];
  currentTransaction: PaymentTransaction | null = null;
  transactionHistory: PaymentTransaction[] = [];

  // Processing state
  isInitializingPayment = false;
  isVerifyingPayment = false;
  verificationAttempts = 0;
  maxVerificationAttempts = 10;
  verificationInterval: NodeJS.Timeout | null = null;
  verificationDeadline: number | null = null;
  private verificationGeneration = 0;
  checkoutObservationState: 'idle' | 'checking' | 'restored' | 'sign_in' | 'unavailable' | 'support' = 'idle';
  checkoutObservationStorageUnavailable = false;
  private checkoutObservationScope: { accountId: string; checkoutId: string; epoch: number } | null = null;
  private checkoutRestorationGeneration = 0;

  // Telco mandate state
  /** Backend-provided flag indicating if user needs to approve on phone. Null if backend doesn't provide this. */
  serverRequiresUserApproval: boolean | null = null;
  /** Whether the countdown timer has expired (user can now manually verify) */
  timerExpired = false;

  private readonly checkoutStore = this.container.checkoutStore;

  constructor(private container: IContainer,  private paymentService: ApiPaymentService) {
    makeAutoObservable(this);
    reaction(() => this.container.authStore?.currentUser?.id, (accountId, previousAccountId) => {
      if (accountId) setCheckoutObservationAccount(String(accountId));
      if (previousAccountId && previousAccountId !== accountId) this.suspendCheckoutObservation();
    });
  }

  private currentCheckoutObservationScope() {
    const auth = this.container.authStore;
    const checkout = this.checkoutStore.currentCheckoutSession;
    const accountId = auth?.isHydrated && auth.isAuthenticated ? String(auth.currentUser?.id || '') : '';
    if (!accountId || !checkout?.account_id || accountId !== checkout.account_id) return null;
    setCheckoutObservationAccount(accountId);
    return { accountId, checkoutId: checkout.session_id, epoch: checkoutObservationEpoch() };
  }

  private checkoutScopeIsCurrent(scope: NonNullable<PaymentStore['checkoutObservationScope']>): boolean {
    const current = this.currentCheckoutObservationScope();
    return Boolean(current && current.accountId === scope.accountId && current.checkoutId === scope.checkoutId
      && current.epoch === scope.epoch);
  }

  private setCheckoutObservationState(state: PaymentStore['checkoutObservationState']): void {
    this.checkoutObservationState = state;
  }

  get hasCheckoutObservationError(): boolean {
    return this.checkoutObservationState === 'sign_in' || this.checkoutObservationState === 'unavailable'
      || this.checkoutObservationState === 'support';
  }

  hasUnsavedCheckoutObservation(checkoutId?: string): boolean {
    const accountId = String(this.container.authStore?.currentUser?.id || '');
    if (!accountId) return false;
    return getKnownCheckoutPaymentObservations(accountId).some((observation) => {
      if (checkoutId && observation.checkoutId !== checkoutId) return false;
      const saved = readCheckoutPaymentObservation(accountId, observation.checkoutId);
      return saved.status !== 'saved' || saved.value.paymentReference !== observation.paymentReference;
    });
  }

  requireCheckoutAuthentication(checkoutId: string): boolean {
    const canSignIn = !this.hasUnsavedCheckoutObservation(checkoutId);
    this.checkoutObservationState = canSignIn ? 'sign_in' : 'support';
    this.stopPaymentVerification();
    return canSignIn;
  }

  suspendCheckoutObservation(): void {
    this.checkoutRestorationGeneration += 1;
    if (this.checkoutObservationScope) this.clearCurrentTransaction();
    this.checkoutObservationScope = null;
    this.checkoutObservationState = 'idle';
  }

  finishCheckoutObservation(): void {
    const scope = this.checkoutObservationScope || this.currentCheckoutObservationScope();
    if (scope && this.checkoutScopeIsCurrent(scope)) {
      removeCheckoutPaymentObservation(scope.accountId, scope.checkoutId);
    }
  }

  async restoreCheckoutPaymentObservation(checkoutId: string): Promise<boolean> {
    const generation = ++this.checkoutRestorationGeneration;
    const scope = this.currentCheckoutObservationScope();
    if (scope?.checkoutId === checkoutId && this.checkoutStore.currentCheckoutSession?.status === 'completed') {
      removeCheckoutPaymentObservation(scope.accountId, checkoutId);
      if (this.checkoutObservationScope?.checkoutId === checkoutId && this.currentTransaction?.orderId === checkoutId) {
        this.clearCurrentTransaction();
      }
      this.checkoutObservationState = 'idle';
      return false;
    }
    if (this.hasUnsavedCheckoutObservation(checkoutId)) {
      this.requireCheckoutAuthentication(checkoutId);
      return false;
    }
    if (!scope || scope.checkoutId !== checkoutId) {
      this.checkoutObservationState = 'sign_in';
      return false;
    }
    if (this.currentTransaction?.details?.intent === 'additional_charge'
      && this.currentTransaction.orderId !== checkoutId) {
      // Capture any outstanding initialization response before changing the viewed payment context.
      if (this.isInitializingPayment) return false;
      this.clearCurrentTransaction();
    }
    const saved = readCheckoutPaymentObservation(scope.accountId, checkoutId);
    if (saved.status !== 'saved') {
      this.checkoutObservationStorageUnavailable = saved.status === 'unsupported';
      this.checkoutObservationState = saved.status === 'unavailable' ? 'unavailable' : 'idle';
      return false;
    }
    this.checkoutObservationState = 'checking';
    const reference = saved.value.paymentReference;
    const isCurrent = () => generation === this.checkoutRestorationGeneration && this.checkoutScopeIsCurrent(scope);
    try {
      const detail = await this.paymentService.getPaymentStatus(reference);
      if (!isCurrent()) return false;
      if (detail.paymentId !== reference || detail.checkoutSessionId !== checkoutId) {
        this.setCheckoutObservationState('unavailable');
        return false;
      }
      this.stopPaymentVerification();
      runInAction(() => {
        this.checkoutObservationScope = scope;
        this.currentTransaction = {
          id: reference, reference, orderId: checkoutId, amount: detail.amount,
          method: PaymentMethodType.MOBILE_MONEY, provider: '', status: 'processing',
          createdAt: new Date(saved.value.savedAt), updatedAt: new Date(),
        };
      });
      await this.verifyPayment(reference, reference, PaymentMethodType.MOBILE_MONEY);
      if (!isCurrent() || this.hasCheckoutObservationError) return false;
      this.setCheckoutObservationState('restored');
      if (this.currentTransaction?.status === 'processing' && this.currentTransaction.customerPaymentDecision !== 'available') {
        this.startPaymentVerification(reference, reference, PaymentMethodType.MOBILE_MONEY, {
          pollIntervalMs: 10_000,
          timeoutSeconds: 60,
        });
      }
      return true;
    } catch (error) {
      if (isCurrent()) this.setCheckoutObservationState(isAxiosError(error) && [401, 403].includes(error.response?.status || 0)
        ? 'sign_in' : 'unavailable');
      return false;
    }
  }

  // Computed properties
  get hasPendingTransaction(): boolean {
    return this.currentTransaction?.status === 'pending' ||
      (
        this.currentTransaction?.status === 'processing' &&
        this.currentTransaction.customerPaymentDecision !== 'available'
      );
  }

  get canRetryPayment(): boolean {
    return this.currentTransaction?.status === 'failed' &&
      !this.isLoading;
  }

  get isPaymentInProgress(): boolean {
    return this.isSessionCleanupInProgress || this.isInitializingPayment ||
      this.isVerifyingPayment ||
      this.hasPendingTransaction;
  }

  get isSessionCleanupInProgress(): boolean {
    return Boolean(this.container.authStore?.isSigningOut);
  }

  /**
   * Determines if user needs to manually approve the payment on their phone.
   * Uses backend value if available, otherwise falls back to heuristic based on auto_debit.enabled.
   *
   * - true: User needs to approve on phone (show countdown + button)
   * - false: Auto-debit will happen (show simple spinner)
   */
  get requiresUserApproval(): boolean {
    const decision = this.currentTransaction?.customerPaymentDecision;
    if (decision === 'action_required') return true;
    if (decision === 'processing') return false;
    // If backend provides explicit field, use it
    if (this.serverRequiresUserApproval !== null) {
      return this.serverRequiresUserApproval;
    }
    // Fallback heuristic: if auto_debit not enabled, user needs to approve
    const checkoutSession = this.checkoutStore.currentCheckoutSession;
    const autoDebitEnabled = (checkoutSession as any)?.auto_debit?.enabled === true;
    return !autoDebitEnabled;
  }

  // Actions
  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  setInitializingPayment(initializing: boolean): void {
    this.isInitializingPayment = initializing;
  }

  setVerifyingPayment(verifying: boolean): void {
    this.isVerifyingPayment = verifying;
  }

  setTimerExpired(expired: boolean): void {
    this.timerExpired = expired;
  }

  setServerRequiresUserApproval(requires: boolean | null): void {
    this.serverRequiresUserApproval = requires;
  }

  getLockedPaymentPhoneNumber(phoneNumber: string | null | undefined): string {
    return String(phoneNumber || '').trim();
  }

  getCustomerVisiblePssPaymentOptions(
    options: AvailablePaymentMethod[]
  ): AvailablePaymentMethod[] {
    const launchedOptions = options.filter(
      (option) => option.available !== false && !this.isTelecelPaymentOption(option)
    );
    if (launchedOptions.some((option) => option.is_default)) {
      return launchedOptions;
    }
    return launchedOptions.map((option, index) => ({
      ...option,
      is_default: index === 0,
    }));
  }

  getDefaultPssConsentProvider(options: AvailablePaymentMethod[]): CheckoutConsentProvider | null {
    const mobileOptions = this.getCustomerVisiblePssPaymentOptions(options).filter(
      (option) => this.mapPaymentOptionTypeToProvider(option.type) !== null,
    );
    const selected = mobileOptions.find((option) => option.is_default) ?? mobileOptions[0];
    return selected ? this.mapPaymentOptionTypeToProvider(selected.type) : null;
  }

  cacheTransaction(request: PaymentInitiationRequest){
    runInAction(() => {
      this.currentTransaction = {
        id: 'txn_' + Math.random().toString(36).substring(2, 15),
        orderId: request.orderId,
        installmentId: request.installmentId,
        amount: request.amount,
        method: request.method,
        status: 'pending',
        phoneNumber: request.phoneNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
        details: request,
        provider: request.provider
      }
    });
  }

  // MTN Mobile Money Payment
  private initializationCleanupFailure(): PaymentResponse | null {
    if (!this.isSessionCleanupInProgress) return null;
    const message = 'Please finish signing in before making a payment.';
    if (this.currentTransaction?.status === 'pending' && !this.currentTransaction.reference
      && !this.isInitializingPayment && !this.isVerifyingPayment) {
      this.updateTransaction({ status: 'failed', failureReason: message });
    }
    return { success: false, message };
  }

  async initializeMobileMoneyPayment(request: PaymentInitiationRequest): Promise<PaymentResponse> {
    const cleanupFailure = this.initializationCleanupFailure();
    if (cleanupFailure) return cleanupFailure;
    const transactionId = this.currentTransaction?.id;
    const observationScope = this.currentCheckoutObservationScope();
    try {
      if (!transactionId) {
        throw new Error('No current transaction to initialize payment for');
      }
      this.setInitializingPayment(true);
      this.setError(null);
      this.setServerRequiresUserApproval(null);
      this.setTimerExpired(false);
      // Create transaction
      const transaction = this.createTransaction(request);
      if (transaction.intent === 'checkout') this.checkoutObservationScope = observationScope;

      const response = await this.paymentService.initializePayment(transaction)
      if (!this.isCurrentTransaction(transactionId) || (observationScope && !this.checkoutScopeIsCurrent(observationScope))) {
        return {
          success: false,
          superseded: true,
          message: 'Payment initialization failed',
        };
      }

      if (response) {
        if (transaction.intent === 'checkout' && observationScope && response.paymentId) {
          const saved = saveCheckoutPaymentObservation(observationScope.accountId, observationScope.checkoutId, response.paymentId);
          runInAction(() => {
            this.checkoutObservationScope = observationScope;
            this.checkoutObservationStorageUnavailable = !saved;
          });
        }
        this.updateTransaction({
          status: 'processing',
          reference: response.paymentId,
        });

        // Store backend-provided requiresUserApproval if available
        if (response.requiresUserApproval !== undefined) {
          this.setServerRequiresUserApproval(response.requiresUserApproval);
        }

        // Start verification polling
        this.startPaymentVerification(
          transactionId,
          response.paymentId,
          PaymentMethodType.MOBILE_MONEY,
          {
            pollIntervalMs: Math.max((response.pollingInterval || 5) * 1000, 1000),
            timeoutSeconds: Math.max(response.timeoutSeconds || 60, 30),
          }
        );
        this.updateTransaction({
          instructions: response.instructions.message ?? ''
        })
        return {
          success: true,
          transactionId: transaction.idempotencyKey,
          reference: response.paymentId,
          message: 'Payment initiated successfully. Please complete the payment on your phone.',
          pollInterval: Math.max((response.pollingInterval || 5) * 1000, 1000),
        };
      } else {
        this.updateTransaction({
          status: 'failed',
          failureReason: 'Payment initialization failed',
        });

        throw new Error('Payment initialization failed');
      }
    } catch (error: any) {
      if (transactionId && !this.isCurrentTransaction(transactionId)) {
        return {
          success: false,
          superseded: true,
          message: 'Payment initialization failed',
        };
      }
      const message = getApiErrorMessage(error, 'Failed to initialize payment.', {
        conflictMessage: 'A similar payment request is already being processed. Please wait a moment and check the current payment status before trying again.',
      });

      this.updateTransaction({
        status: 'failed',
        failureReason: message,
      });

      runInAction(() => {
        this.setError(message);
      });

      return {
        success: false,
        message,
      };
    } finally {
      if (!transactionId || this.isCurrentTransaction(transactionId)) {
        this.setInitializingPayment(false);
      }
    }
  }


//   {
//   "intent": "checkout",
//     "context": {
//     "checkout_session_id": "d80dfedc-9574-4f21-92d9-928c130fef55",
//       "order_id": "d80dfedc-9574-4f21-92d9-928c130fef55",
//         "installment_id": "d80dfedc-9574-4f21-92d9-928c130fef55",
//           "amount": "935"
//   },
//   "payment_provider": "mtn",
//     "payment_method": "mobile_money",
//       "phone_number": "+233553379296",
//         "idempotency_key": "1761578731783-o775eyfij"
// }

  async registerPayment(
    request: z.infer<typeof paymentMethodTypeSchema> & { autoDebitAuthorized?: boolean },
    amount: number,
    orderId: string,
    installmentId?: string,
    intent?: PaymentIntent,
    chargeId?: string,
    recoveryPaymentContext?: PaymentInitiationRequest['recoveryPaymentContext'],
    walletPlanApplication?: PaymentInitiationRequest['walletPlanApplication'],
  ) {
    this.setServerRequiresUserApproval(null);
    this.setTimerExpired(false);
    const paymentOptionType = String(request.mobileNetwork || '').trim().toLowerCase();
    const provider = this.mapPaymentOptionTypeToProvider(paymentOptionType);
    const idempotencyKey =
      walletPlanApplication?.providerIdempotencyKey || `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    if (!provider) {
      throw new Error('Unsupported payment option');
    }

    const payload: PaymentInitiationRequest = {
      amount: Number(amount),
      orderId,
      installmentId,
      chargeId,
      recoveryPaymentContext,
      walletPlanApplication,
      phoneNumber: request.phoneNumber,
      method: request.type as PaymentMethodType,
      provider,
      idempotencyKey,
      // Exchange top-ups are one-off additional charges; do not opt the customer into recurring debit consent.
      autoDebitAuthorized: intent === 'additional_charge' ? false : (request.autoDebitAuthorized ?? true),
      intent,
    }
    const transaction = this.createTransaction(payload);

    runInAction(() => {
      this.currentTransaction = {
        id: 'txn_' + Math.random().toString(36).substring(2, 15),
        orderId: payload.orderId,
        installmentId: payload.installmentId,
        amount: payload.amount,
        method: payload.method,
        status: 'pending',
        phoneNumber: payload.phoneNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
        details: payload,
        provider: payload.provider
      }
    });

    return transaction;
}

  // Generic payment initialization
  async initializePayment(): Promise<PaymentResponse> {
    const cleanupFailure = this.initializationCleanupFailure();
    if (cleanupFailure) return cleanupFailure;
    if (!this.currentTransaction?.details) {
      return {
        success: false,
        message: 'No current transaction to initialize payment for',
      };
    }
    if (this.isInitializingPayment || this.currentTransaction.status === 'processing') {
      return {
        success: false,
        message: 'A payment request is already being processed. Please wait for the prompt on your phone or check the payment status.',
      };
    }
    switch (this.currentTransaction!.method) {
      case PaymentMethodType.MOBILE_MONEY:
        return this.initializeMobileMoneyPayment(this.currentTransaction!.details);
      case PaymentMethodType.BANK_TRANSFER:
        return this.initializeEcobankPayment({} as PaymentInitiationRequest);
      default:
        return {
          success: false,
          message: 'Unsupported payment method',
        };
    }
  }

  // Ecobank Payment (placeholder for future implementation)
  async initializeEcobankPayment(_request: PaymentInitiationRequest): Promise<PaymentResponse> {
    // TODO: Implement Ecobank payment integration
    return {
      success: false,
      message: 'Ecobank payment not yet implemented',
    };
  }

  private isCurrentVerification(transactionId: string, expectedGeneration: number): boolean {
    return (
      expectedGeneration === this.verificationGeneration &&
      this.currentTransaction?.id === transactionId &&
      (!this.checkoutObservationScope || this.checkoutScopeIsCurrent(this.checkoutObservationScope))
    );
  }

  private isCurrentTransaction(transactionId: string): boolean {
    return this.currentTransaction?.id === transactionId;
  }

  // Payment verification
  async verifyPayment(
    transactionId: string,
    reference: string,
    method: PaymentMethodType,
    expectedGeneration = this.verificationGeneration,
  ): Promise<boolean> {
    try {
      this.setVerifyingPayment(true);

      switch (method) {
        case PaymentMethodType.MOBILE_MONEY:
          // Allow the backend's 60-second request budget plus response transit.
          const response = await this.paymentService.getPaymentStatusPoll(reference, 65_000);
          if (!this.isCurrentVerification(transactionId, expectedGeneration)) {
            return true;
          }
          if (this.checkoutObservationScope && !this.currentTransaction?.details && response.paymentReference !== reference && response.paymentId !== reference) {
            this.setCheckoutObservationState('unavailable');
            this.stopPaymentVerification();
            return true;
          }
          if (this.checkoutObservationScope && response.provider) this.updateTransaction({ provider: response.provider });
          this.updateTransaction({ customerPaymentOutcome: response.customerPaymentOutcome });

          if (response.requiresUserApproval !== undefined) {
            this.setServerRequiresUserApproval(response.requiresUserApproval);
          }

          if (customerPaymentSucceeded(response.customerPaymentDecision)) {
            this.updateTransaction({
              status: 'completed',
              customerPaymentDecision: 'completed',
            });
            this.stopPaymentVerification();
            return true;
          }

          if (response.customerPaymentDecision === 'available') {
            this.updateTransaction({
              status: 'processing',
              customerPaymentDecision: 'available',
              failureReason: undefined,
            });
            this.stopPaymentVerification();
            return true;
          }

          if (customerPaymentUnavailable(response.customerPaymentDecision)) {
            this.updateTransaction({
              status: 'failed',
              customerPaymentDecision: 'unavailable',
              failureReason: customerPaymentFailureReason(response),
            });
            this.stopPaymentVerification();
            return true;
          }

          if (
            response.customerPaymentDecision === 'processing'
            || response.customerPaymentDecision === 'action_required'
          ) {
            this.updateTransaction({
              status: 'processing',
              customerPaymentDecision: response.customerPaymentDecision,
              failureReason: undefined,
            });
            return false;
          }

          if (response.customerPaymentDecision) return false;

          // A response without the additive decision field came from a legacy
          // backend. Do not let a decision from an earlier canonical response
          // leak into the compatibility path.
          this.updateTransaction({ customerPaymentDecision: undefined });

          if (response.status === 'completed') {
            this.updateTransaction({ status: 'completed' });
            this.stopPaymentVerification();
            return true;
          }

          if (['failed', 'cancelled', 'refunded'].includes(response.status)) {
            this.updateTransaction({
              status: 'failed',
              failureReason: customerPaymentFailureReason(response),
            });
            this.stopPaymentVerification();
            return true;
          }

          // Payment still in progress, continue polling.
          return false;
        default:
          throw new Error('Unsupported payment method for verification');
      }
    } catch (error: any) {
      if (!this.isCurrentVerification(transactionId, expectedGeneration)) {
        return true;
      }
      this.setError(error?.message ? String(error.message) : 'Payment verification failed');
      const authenticationFailed = isAxiosError(error) && [401, 403].includes(error.response?.status || 0);
      if (this.checkoutObservationScope && (authenticationFailed || !this.currentTransaction?.details)) {
        if (authenticationFailed) this.requireCheckoutAuthentication(this.checkoutObservationScope.checkoutId);
        else {
          this.setCheckoutObservationState('unavailable');
          this.stopPaymentVerification();
        }
      }
      return false;
    } finally {
      if (this.isCurrentVerification(transactionId, expectedGeneration)) {
        this.setVerifyingPayment(false);
      }
    }
  }

  // Stop payment verification
  stopPaymentVerification(): void {
    this.verificationGeneration += 1;
    this.setVerifyingPayment(false);
    if (this.verificationInterval) {
      clearInterval(this.verificationInterval);
      this.verificationInterval = null;
    }
    this.verificationAttempts = 0;
    this.verificationDeadline = null;
  }

  // Manual payment verification trigger
  async manualVerifyPayment(): Promise<boolean> {
    if (this.isVerifyingPayment) return false;
    const transaction = this.currentTransaction;
    if (!transaction?.reference) {
      throw new Error('No transaction reference available');
    }
    const expectedGeneration = this.verificationGeneration;

    const isTerminal = await this.verifyPayment(
      transaction.id,
      transaction.reference,
      transaction.method,
      expectedGeneration,
    );

    if (isTerminal) return true;
    if (
      !this.isCurrentVerification(transaction.id, expectedGeneration)
      || this.currentTransaction?.reference !== transaction.reference
    ) {
      return true;
    }

    if (!isTerminal) {
      this.stopPaymentVerification();
      this.startPaymentVerification(
        transaction.id,
        transaction.reference,
        transaction.method,
        this.lastVerificationPolicy ?? undefined
      );
    }

    return isTerminal;
  }

  // Cancel payment
  cancelPayment(): void {
    this.stopPaymentVerification();

    if (this.currentTransaction) {
      this.updateTransaction({
        status: 'cancelled',
      });
    }
  }

  // Get transaction by ID
  getTransaction(transactionId: string): PaymentTransaction | undefined {
    return this.transactionHistory.find(t => t.id === transactionId);
  }

  // Get transactions by order
  getTransactionsByOrder(orderId: string): PaymentTransaction[] {
    return this.transactionHistory.filter(t => t.orderId === orderId);
  }

  // Clear current transaction
  clearCurrentTransaction(): void {
    this.stopPaymentVerification();
    runInAction(() => {
      this.currentTransaction = null;
      this.isInitializingPayment = false;
      this.error = null;
      this.verificationAttempts = 0;
      this.checkoutObservationScope = null;
    });
  }

  updateCurrentTransactionAmount(amount: number): void {
    const normalizedAmount = normalizeMoneyString(amount);
    const numericAmount = Number(normalizedAmount);
    if (!normalizedAmount || !Number.isFinite(numericAmount) || numericAmount <= 0) {
      return;
    }

    runInAction(() => {
      if (!this.currentTransaction) {
        return;
      }

      this.currentTransaction.amount = numericAmount;
      if (this.currentTransaction.details) this.currentTransaction.details.amount = numericAmount;
      this.currentTransaction.updatedAt = new Date();
    });
  }

  async previewStoreCreditPlanPayment(
    request: StoreCreditPlanPaymentPreviewRequest
  ): Promise<StoreCreditPlanPaymentPreviewResponse> {
    return this.paymentService.previewStoreCreditPlanPayment(request);
  }

  getPlanPaymentProviderRecoveryPolicy(
    retryAfterSeconds?: number,
  ): PlanPaymentProviderRecoveryPolicy {
    const requestedInterval = Number.isFinite(retryAfterSeconds)
      ? Number(retryAfterSeconds)
      : 15;
    const intervalSeconds = Math.min(
      Math.max(requestedInterval, MIN_PLAN_PAYMENT_PROVIDER_INTERVAL_SECONDS),
      MAX_PLAN_PAYMENT_PROVIDER_INTERVAL_SECONDS,
    );
    return {
      intervalMs: intervalSeconds * 1000,
      requestTimeoutMs: PLAN_PAYMENT_PROVIDER_STATUS_TIMEOUT_MS,
      windowMs: PLAN_PAYMENT_PROVIDER_RECOVERY_WINDOW_MS,
    };
  }

  async refreshPlanPaymentProviderStatus(
    paymentId: string,
    timeoutMs?: number,
  ): Promise<PaymentStatusPollResult> {
    return this.paymentService.getPaymentStatusPoll(paymentId, timeoutMs);
  }

  async applyStoreCreditPlanPayment(
    request: StoreCreditPlanPaymentApplyRequest
  ): Promise<StoreCreditPlanPaymentApplyResponse> {
    if (this.isSessionCleanupInProgress) throw new Error('Please finish signing in before making a payment.');
    const result = await this.paymentService.applyStoreCreditPlanPayment(request);
    if (isPlanWalletConsentPending(result)) throw new Error();
    return result;
  }

  async getCompletedStoreCreditPlanPayment(
    request: StoreCreditPlanPaymentApplyRequest
  ): Promise<StoreCreditPlanPaymentApplyResponse | null> {
    return this.paymentService.getCompletedStoreCreditPlanPayment(request);
  }

  clearPlanWalletConsent(): void {
    this.planWalletConsentGeneration += 1;
    if (this.planWalletConsentTimer) clearTimeout(this.planWalletConsentTimer);
    this.planWalletConsentTimer = null;
    this.planWalletConsentRequest = null;
    this.planWalletConsent = null;
    this.planWalletConsentResult = null;
    this.planWalletConsentError = null;
    this.planWalletConsentPollingPaused = false;
    this.planWalletConsentFailed = false;
    this.isCheckingPlanWalletConsent = false;
    this.planWalletConsentWarningEmitted = false;
  }

  rejectPlanWalletConsentResult(result: StoreCreditPlanPaymentApplyResponse): void {
    if (this.planWalletConsentResult !== result) return;
    // An unreadable result may already have settled; preserve its request for an idempotent recheck.
    this.planWalletConsentResult = null;
    if (this.planWalletConsentTimer) clearTimeout(this.planWalletConsentTimer);
    this.planWalletConsentTimer = null;
    this.planWalletConsentDeadline = Date.now();
    this.planWalletConsentPollingPaused = true;
    this.planWalletConsentFailed = false;
  }

  get planWalletConsentInProgress(): boolean {
    return this.planWalletConsentRequest !== null;
  }

  private recordPlanWalletConsentError(error: unknown): void {
    const response = isAxiosError<{ code?: string }>(error) ? error.response : undefined;
    this.planWalletConsentFailed = (response?.status === 400 && response.data?.code === 'store_credit_plan_payment_failed')
      || (response?.status === 409 && response.data?.code === 'store_credit_plan_payment_conflict');
    this.planWalletConsentError = getApiErrorMessage(error, 'Please try again.');
    // Without a returned agreement, retry the same POST/key rather than inventing a new payment.
    if (!this.planWalletConsent) this.planWalletConsentPollingPaused = true;
  }

  async beginPlanWalletConsent(request: StoreCreditPlanPaymentApplyRequest): Promise<void> {
    if (this.isSessionCleanupInProgress) throw new Error('Please finish signing in before making a payment.');
    if (this.isCheckingPlanWalletConsent || this.planWalletConsentRequest) return;
    this.clearPlanWalletConsent();
    this.planWalletConsentRequest = { ...request };
    this.planWalletConsentDeadline = Date.now() + 90_000;
    await this.submitPlanWalletConsent(false, this.planWalletConsentGeneration);
  }

  async checkPlanWalletConsent(): Promise<void> {
    if (this.isCheckingPlanWalletConsent || this.planWalletConsentResult) return;
    await this.submitPlanWalletConsent(true, this.planWalletConsentGeneration);
  }

  async retryPlanWalletConsent(): Promise<boolean> {
    if (this.isSessionCleanupInProgress) return false;
    if (this.isCheckingPlanWalletConsent || !this.planWalletConsentRequest) return false;
    const generation = this.planWalletConsentGeneration;
    await this.checkPlanWalletConsent();
    if (generation !== this.planWalletConsentGeneration || !this.planWalletConsentFailed) return false;
    this.clearPlanWalletConsent();
    return true;
  }

  private async submitPlanWalletConsent(refresh: boolean, generation: number): Promise<void> {
    const request = this.planWalletConsentRequest;
    if (!request || generation !== this.planWalletConsentGeneration || this.isCheckingPlanWalletConsent) return;
    if (this.isSessionCleanupInProgress) {
      this.planWalletConsentError = 'Please finish signing in before making a payment.';
      this.planWalletConsentPollingPaused = true;
      return;
    }
    if (this.planWalletConsentTimer) clearTimeout(this.planWalletConsentTimer);
    this.planWalletConsentTimer = null;
    this.isCheckingPlanWalletConsent = true;
    this.planWalletConsentError = null;
    this.planWalletConsentFailed = false;
    try {
      const result = await this.paymentService.applyStoreCreditPlanPayment({ ...request,
        ...(this.planWalletConsent?.agreement_id ? { consentAgreementId: this.planWalletConsent.agreement_id } : {}),
        refreshConsent: refresh });
      if (generation !== this.planWalletConsentGeneration) return;
      if (isPlanWalletConsentPending(result)) {
        if (result.order_id !== request.orderId || result.intent !== request.intent
            || normalizeMoneyString(result.wallet_amount) !== normalizeMoneyString(request.expectedWalletAmount)
            || normalizeMoneyString(result.selected_payment_amount) !== normalizeMoneyString(request.expectedWalletAmount)) {
          throw new Error();
        }
        runInAction(() => { this.planWalletConsent = result; });
      } else {
        runInAction(() => { this.planWalletConsentResult = result; });
      }
    } catch (error: unknown) {
      if (generation === this.planWalletConsentGeneration) runInAction(() => {
        this.recordPlanWalletConsentError(error);
      });
    } finally {
      if (generation === this.planWalletConsentGeneration) {
        runInAction(() => { this.isCheckingPlanWalletConsent = false; });
        this.schedulePlanWalletConsentPoll(generation);
      }
    }
  }

  private schedulePlanWalletConsentPoll(generation: number): void {
    if (generation !== this.planWalletConsentGeneration || !this.planWalletConsent || this.planWalletConsentResult
        || this.planWalletConsentFailed || this.planWalletConsentTimer || this.isCheckingPlanWalletConsent) return;
    if (Date.now() >= this.planWalletConsentDeadline) {
      this.planWalletConsentPollingPaused = true;
      return;
    }
    this.planWalletConsentTimer = setTimeout(() => {
      runInAction(() => { this.planWalletConsentTimer = null; });
      void this.pollPlanWalletConsent(generation);
    }, Math.min(5_000, this.planWalletConsentDeadline - Date.now()));
  }

  private async pollPlanWalletConsent(generation: number): Promise<void> {
    const request = this.planWalletConsentRequest;
    const consent = this.planWalletConsent;
    if (!request || !consent || generation !== this.planWalletConsentGeneration) return;
    if (Date.now() >= this.planWalletConsentDeadline) {
      this.planWalletConsentPollingPaused = true;
      return;
    }
    this.isCheckingPlanWalletConsent = true;
    let active = false;
    try {
      const preview = await this.paymentService.previewStoreCreditPlanPayment({ ...request,
        consentProvider: 'mtn', consentAgreementId: consent.agreement_id });
      if (generation !== this.planWalletConsentGeneration) return;
      active = preview.consent?.status === 'active' && preview.consent.agreement_id === consent.agreement_id;
      if (preview.consent?.status === 'required' && preview.consent.agreement_id === consent.agreement_id) {
        runInAction(() => { this.planWalletConsentFailed = true; });
      }
    } catch (error: unknown) {
      if (generation === this.planWalletConsentGeneration) runInAction(() => {
        this.recordPlanWalletConsentError(error);
        if (!this.planWalletConsentWarningEmitted) {
          this.planWalletConsentWarningEmitted = true;
          captureMessage('pay_early.wallet_consent_status_unavailable', { level: 'warning' });
        }
      });
    } finally {
      if (generation === this.planWalletConsentGeneration) runInAction(() => { this.isCheckingPlanWalletConsent = false; });
    }
    if (generation !== this.planWalletConsentGeneration) return;
    if (active) await this.submitPlanWalletConsent(false, generation);
    else this.schedulePlanWalletConsentPoll(generation);
  }

  // Reset store
  reset(): void {
    this.suspendCheckoutObservation();
    this.clearPlanWalletConsent();
    this.stopPaymentVerification();
    runInAction(() => {
      this.isLoading = false;
      this.error = null;
      this.currentTransaction = null;
      this.transactionHistory = [];
      this.isInitializingPayment = false;
      this.isVerifyingPayment = false;
      this.verificationAttempts = 0;
      this.serverRequiresUserApproval = null;
      this.timerExpired = false;
      this.lastVerificationPolicy = null;
    });
  }

  // Create new transaction
  private createTransaction(request: PaymentInitiationRequest): PaymentInitializationParams {
    const checkoutSessionId = this.checkoutStore.currentCheckoutSession?.session_id;
    const isCheckoutPayment = Boolean(checkoutSessionId) && !request.installmentId && !request.intent;
    const normalizedAmount = normalizeMoneyString(request.amount);

    if (!normalizedAmount) {
      throw new Error('Invalid payment amount');
    }

    // Determine intent: use provided intent, or derive from context
    let intent: PaymentIntent;
    if (request.intent) {
      intent = request.intent;
    } else if (isCheckoutPayment) {
      intent = 'checkout';
    } else {
      intent = 'installment';
    }

    // Build context based on intent
    let context: PaymentInitializationParams['context'];
    if (intent === 'checkout') {
      context = {
        checkout_session_id: checkoutSessionId,
        amount: normalizedAmount,
      };
    } else if (intent === 'additional_charge') {
      context = {
        order_id: request.orderId,
        charge_id: request.chargeId,
        amount: normalizedAmount,
      };
    } else if (intent === 'payoff') {
      context = {
        order_id: request.orderId,
        ...(request.installmentId && { installment_id: request.installmentId }),
      };
    } else {
      // For installment and advance - use order_id plus the normalized amount.
      context = {
        order_id: request.orderId,
        ...(request.installmentId && { installment_id: request.installmentId }),
        amount: normalizedAmount,
        ...(request.recoveryPaymentContext?.paymentPlanId && {
          payment_plan_id: request.recoveryPaymentContext.paymentPlanId,
        }),
        ...(request.recoveryPaymentContext?.recoveryOptionId && {
          recovery_option_id: request.recoveryPaymentContext.recoveryOptionId,
        }),
        ...(request.recoveryPaymentContext?.recoveryReliefId && {
          recovery_relief_id: request.recoveryPaymentContext.recoveryReliefId,
        }),
      };
    }
    const metadata: PaymentInitializationParams['metadata'] | undefined = request.walletPlanApplication
      ? {
          wallet_plan_application_id: request.walletPlanApplication.applicationId,
          wallet_amount_applied: normalizeMoneyString(request.walletPlanApplication.amountApplied),
          ...(request.walletPlanApplication.selectedAmount !== undefined
            ? { wallet_selected_payment_amount: normalizeMoneyString(request.walletPlanApplication.selectedAmount) }
            : {}),
        }
      : undefined;

    const transaction: PaymentInitializationParams = {
      provider: request.provider,
      method: request.method,
      phoneNumber: request.phoneNumber,
      intent,
      autoDebitAuthorized: request.autoDebitAuthorized ?? true, // Always true for PSS
      amount: normalizedAmount,
      context,
      metadata,
      idempotencyKey: request.idempotencyKey,
    };

    return transaction;
  }

  private mapPaymentOptionTypeToProvider(optionType: string): CheckoutConsentProvider | null {
    switch (optionType) {
      case 'mtn_mobile_money':
        return 'mtn';
      case 'telecel_mobile_money':
        return 'telecel';
      case 'debit_card':
        // Card is processed via Paystack but is intentionally not exposed in the current UI flow.
        return null;
      default:
        return null;
    }
  }

  private isTelecelPaymentOption(option: AvailablePaymentMethod): boolean {
    const value = `${option.type || ''} ${option.provider || ''} ${option.display_name || ''}`.toLowerCase();
    return value.includes('telecel') || value.includes('vodafone');
  }

  // Update transaction
  private updateTransaction(updates: Partial<PaymentTransaction>): void {
    if (!this.currentTransaction) return;

    runInAction(() => {
      if (this.currentTransaction) {
        Object.assign(this.currentTransaction, {
          ...updates,
          updatedAt: new Date(),
        });

        // Update in history as well
        const historyIndex = this.transactionHistory.findIndex(
          t => t.id === this.currentTransaction!.id
        );
        if (historyIndex >= 0) {
          Object.assign(this.transactionHistory![historyIndex]!, {
            ...updates,
            updatedAt: new Date(),
          });
        }
      }
    });
  }

  // Start automatic payment verification
  private startPaymentVerification(
    transactionId: string,
    reference: string,
    method: PaymentMethodType,
    policy: { pollIntervalMs: number; timeoutSeconds: number } = {
      pollIntervalMs: 3000,
      timeoutSeconds: 60,
    }
  ): void {
    this.startPaymentVerificationWithPolicy(transactionId, reference, method, policy);
  }

  private startPaymentVerificationWithPolicy(
    transactionId: string,
    reference: string,
    method: PaymentMethodType,
    policy: { pollIntervalMs: number; timeoutSeconds: number }
  ): void {
    if (this.verificationInterval) {
      clearInterval(this.verificationInterval);
      this.verificationInterval = null;
    }
    const verificationGeneration = ++this.verificationGeneration;
    const pollIntervalMs = Math.max(policy.pollIntervalMs, 1000);
    const timeoutSeconds = Math.max(policy.timeoutSeconds, 30);
    this.lastVerificationPolicy = { pollIntervalMs, timeoutSeconds };

    this.verificationAttempts = 0;
    this.maxVerificationAttempts = Math.max(
      Math.ceil((timeoutSeconds * 1000) / pollIntervalMs),
      1,
    );
    this.verificationDeadline = Date.now() + timeoutSeconds * 1000;

    this.verificationInterval = setInterval(async () => {
      // The same payment's bounded read may finish after the observation window.
      if (this.isVerifyingPayment) return;
      if (this.verificationDeadline && Date.now() > this.verificationDeadline) {
        this.setTimerExpired(true);
        this.stopPaymentVerification();
        return;
      }
      this.verificationAttempts++;

      const isTerminal = await this.verifyPayment(
        transactionId,
        reference,
        method,
        verificationGeneration,
      );

      if (isTerminal) {
        // verifyPayment() already updates state + stops polling for terminal states
        return;
      }

      if (this.verificationAttempts >= this.maxVerificationAttempts) {
        this.setTimerExpired(true);
        this.stopPaymentVerification();
      }
    }, pollIntervalMs);
  }

  // Validation
  private validatePaymentRequest(request: PaymentInitiationRequest): { isValid: boolean; message: string } {
    if (!request.amount || request.amount <= 0) {
      return { isValid: false, message: 'Invalid payment amount' };
    }

    if (!request.orderId) {
      return { isValid: false, message: 'Order ID is required' };
    }

    if (!request.phoneNumber) {
      return { isValid: false, message: 'Phone number is required' };
    }

    // Validate phone number format (Ghana)
    const phoneRegex = /^(\+233|0)(2[0-9]|5[0-4])[0-9]{7}$/;
    if (!phoneRegex.test(request.phoneNumber)) {
      return { isValid: false, message: 'Invalid phone number format' };
    }

    return { isValid: true, message: '' };
  }
}

export { PaymentStore };
